// components/Sender/SenderDashboard.jsx
import React, { useState, useEffect, useRef } from "react";
import { SenderApi, TOKEN_KEY } from "./SenderApi";
import { SenderTemplatesTab } from "./SenderTemplatesTab";
import { SenderSegmentsTab } from "./SenderSegmentsTab";
import { SenderLogTab } from "./SenderLogTab";
import { SenderSettingsTab } from "./SenderSettingsTab";
import { SenderLocalRunsTab } from "./SenderLocalRunsTab";
import { SenderRuntimesTab } from "./SenderRuntimesTab";
import { MessageDetailDialog } from "./MessageDetailDialog";
import { TestSendDialog } from "./TestSendDialog";
import { SenderPlanModal } from "./SenderPlanModal";
import { SenderFinishedModal } from "./SenderFinishedModal";
import { flattenWorkers } from "./workers";

const STATUS_CFG = {
  idle:     { label: "Готов",             bg: "#dcfce7", color: "#166534" },
  running:  { label: "▶ Выполняется",     bg: "#dbeafe", color: "#1d4ed8" },
  stopping: { label: "⏹ Останавливается", bg: "#fef3c7", color: "#b45309" },
  error:    { label: "✖ Ошибка",          bg: "#fee2e2", color: "#b91c1c" },
  offline:  { label: "🔌 Оффлайн",         bg: "#f3f4f6", color: "#6b7280" },
};

export function SenderDashboard({ add }) {
  const [activeTab, setActiveTab] = useState(
    () => localStorage.getItem("senderActiveTab") || "templates"
  );

  const [status, setStatus] = useState({ state: "idle", progress: null,
                                         runtimes: [] });
  const [segments, setSegments] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [workersError, setWorkersError] = useState(null);
  const [campaigns, setCampaigns] = useState({});
  const [templates, setTemplates] = useState([]);
  const [runtimes, setRuntimes] = useState([]);
  const [localRuntimes, setLocalRuntimes] = useState([]);
  const [emailAccounts, setEmailAccounts] = useState([]);

  const [selectedRunId, setSelectedRunId] = useState(null);
  const [logs, setLogs] = useState([]);
  const [lastRun, setLastRun] = useState(null);
  const [logsResetKey, setLogsResetKey] = useState(0);
  const logCursorRef = useRef(0);
  const lastRunIdRef = useRef(null);

  // bootLoading теперь — только «первичная загрузка», а не блокирующий экран.
  // Показываем скелетоны в табах, шапку — сразу.
  const [initialLoading, setInitialLoading] = useState(true);
  const [localOnline, setLocalOnline] = useState(null);
  const [pingKey, setPingKey] = useState(0);
  const [clearingLogs, setClearingLogs] = useState(false);

  const [settings, setSettings] = useState(() => {
    try { return { token: localStorage.getItem(TOKEN_KEY) || "" }; }
    catch { return { token: "" }; }
  });

  const [messageInfo, setMessageInfo] = useState(null);
  const [testSendOpen, setTestSendOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [planTarget, setPlanTarget] = useState(null);
  const [finishedInfo, setFinishedInfo] = useState(null);

  const prevRunningRunIdRef = useRef(null);
  const statusInFlight = useRef(false);

  useEffect(() => {
    localStorage.setItem("senderActiveTab", activeTab);
  }, [activeTab]);

  useEffect(() => {
    const token = settings?.token || (() => {
      try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
    })();
    if (!token) return;
    SenderApi.saveLocalSettings({
      outreach_base: SenderApi.globalOutreachBase,
      outreach_token: token,
    }).catch(() => { /* */ });
  }, [settings?.token]);

  // ─── загрузчики ───
  const loadWorkers = async () => {
    const t0 = performance.now();
    try {
      const raw = await SenderApi.listWorkers();
      const list = flattenWorkers(raw);
      console.log(
        `[Sender] listWorkers: ${list.length} воркеров ` +
        `за ${(performance.now() - t0).toFixed(0)}ms`);
      if (list.length === 0) setWorkersError("Бэкенд вернул 0 воркеров");
      else setWorkersError(null);
      setWorkers(list);
      return list;
    } catch (e) {
      console.error(
        `[Sender] listWorkers FAIL за ${(performance.now() - t0).toFixed(0)}ms:`,
        e);
      setWorkersError(e.message);
      return [];
    }
  };

  const loadCampaigns = async () => {
    const t0 = performance.now();
    try {
      const raw = await SenderApi.listCampaigns();
      const arr = Array.isArray(raw) ? raw : (raw?.results || []);
      const map = {};
      arr.forEach((c) => { map[String(c.id)] = c; });
      setCampaigns(map);
      console.log(
        `[Sender] listCampaigns: ${arr.length} за ` +
        `${(performance.now() - t0).toFixed(0)}ms`);
    } catch (e) {
      console.error("[Sender] listCampaigns FAIL:", e);
    }
  };

  const loadGlobalRuntimes = async () => {
    const t0 = performance.now();
    try {
      const list = await SenderApi.listRuntimes();
      setRuntimes(list || []);
      console.log(
        `[Sender] listRuntimes: ${list?.length || 0} за ` +
        `${(performance.now() - t0).toFixed(0)}ms`);
    } catch (e) {
      console.warn("[Sender] listRuntimes FAIL:", e.message);
    }
  };

    const loadEmailAccounts = async () => {
    const t0 = performance.now();
    try {
      const res = await SenderApi.listEmailAccounts();
      const list = res?.accounts || [];
      setEmailAccounts(list);
      console.log(
        `[Sender] listEmailAccounts: ${list.length} за ` +
        `${(performance.now() - t0).toFixed(0)}ms`);
    } catch (e) {
      console.warn("[Sender] listEmailAccounts FAIL:", e.message);
    }
  };


  const loadSegments = async () => {
    const t0 = performance.now();
    try {
      const list = await SenderApi.listSegments();
      setSegments(list || []);
      console.log(
        `[Sender] listSegments: ${list?.length || 0} за ` +
        `${(performance.now() - t0).toFixed(0)}ms`);
    } catch (e) {
      console.warn("[Sender] listSegments FAIL:", e.message);
    }
  };

  const loadTemplates = async () => {
    const t0 = performance.now();
    try {
      const list = await SenderApi.listTemplates();
      setTemplates(list || []);
      console.log(
        `[Sender] listTemplates: ${list?.length || 0} за ` +
        `${(performance.now() - t0).toFixed(0)}ms`);
    } catch (e) {
      console.warn("[Sender] listTemplates FAIL:", e.message);
    }
  };

  // ─── INITIAL LOAD ─── всё параллельно, без блокировки UI ───
  useEffect(() => {
    const t0 = performance.now();
    (async () => {
      // ждём все 4 запроса параллельно, но UI не блокируем дольше 100ms
      const timer = setTimeout(() => setInitialLoading(false), 100);

      await Promise.allSettled([
        loadSegments(),
        loadWorkers(),
        loadCampaigns(),
        loadGlobalRuntimes(),
        loadEmailAccounts(),

        loadTemplates(),
      ]);

      clearTimeout(timer);
      console.log(
        `[Sender] initial load done за ` +
        `${(performance.now() - t0).toFixed(0)}ms`);
      setInitialLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── polling статуса (inFlight guard) ───
  useEffect(() => {
    let mounted = true;
    let intervalId = null;

    const tick = async () => {
      if (statusInFlight.current) {
        // предыдущий запрос ещё не вернулся — пропускаем этот тик,
        // чтобы не наслаивать
        return;
      }
      statusInFlight.current = true;
      const t0 = performance.now();
      try {
        const st = await SenderApi.getStatus();
        if (!mounted) return;
        if (st) {
          setStatus(st);
          setLocalOnline(true);
          setLocalRuntimes(st.runtimes || []);

          const prev = prevRunningRunIdRef.current;
          const cur = st.current_run_id || null;

          if (cur && cur !== lastRunIdRef.current) {
            lastRunIdRef.current = cur;
            setLastRun({
              id: cur,
              template_name: st.current_run_name,
              status: st.state,
            });
            logCursorRef.current = 0;
            setLogs([]);
          }

          if (prev && !cur) {
            try {
              const finishedRun = await SenderApi.getLocalRun(prev);
              if (finishedRun && mounted) {
                setFinishedInfo({
                  status: finishedRun.status || "finished",
                  stats: finishedRun.stats || {},
                  test_run: !!finishedRun.snapshot?.test_run,
                  finished_at: finishedRun.finished_at,
                  error: finishedRun.stats?.error,
                });
                if (lastRunIdRef.current === prev) {
                  setLastRun((lr) => lr && lr.id === prev
                    ? { ...lr, status: finishedRun.status }
                    : lr);
                }
              }
            } catch { /* */ }
            prevRunningRunIdRef.current = null;
          } else if (cur && cur !== prev) {
            prevRunningRunIdRef.current = cur;
          }
        }
      } catch {
        if (mounted) setLocalOnline(false);
      } finally {
        statusInFlight.current = false;
        const dt = performance.now() - t0;
        if (dt > 1000) {
          console.warn(
            `[Sender] getStatus медленный: ${dt.toFixed(0)}ms`);
        }
      }
    };

    tick();

    const active = ["running", "stopping"].includes(status.state);
    const onRelevantTab = ["runs", "log"].includes(activeTab);
    let period = null;
    if (localOnline === false) period = 2000;
    else if (active)           period = 2000;
    else if (onRelevantTab)    period = 5000;

    if (period) intervalId = setInterval(tick, period);

    return () => { mounted = false; if (intervalId) clearInterval(intervalId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, pingKey, status.state, localOnline]);

  // ─── загрузка lastRun при заходе на вкладку "log" ───
  useEffect(() => {
    if (activeTab !== "log") return;
    let mounted = true;
    (async () => {
      try {
        const res = await SenderApi.getLogsLastRun();
        if (!mounted) return;
        const lr = res?.run || null;
        const newId = lr?.id || null;
        if (newId !== lastRunIdRef.current) {
          lastRunIdRef.current = newId;
          logCursorRef.current = 0;
          setLogs([]);
        }
        setLastRun(lr);
      } catch { /* */ }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, pingKey, logsResetKey]);

  // ─── polling логов последнего run'а ───
  useEffect(() => {
    if (activeTab !== "log") return;
    if (!localOnline) return;
    if (!lastRun?.id) return;

    let mounted = true;
    let inFlight = false;
    let cursor = logCursorRef.current;
    const runId = lastRun.id;

    const tick = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const res = await SenderApi.getLogs(cursor, runId);
        if (!mounted || !res) return;
        if (Array.isArray(res.logs) && res.logs.length) {
          setLogs((prev) => {
            const merged = prev.concat(res.logs);
            return merged.length > 5000 ? merged.slice(-5000) : merged;
          });
          cursor = res.last_id || cursor;
          logCursorRef.current = cursor;
        }
      } catch { /* */ } finally {
        inFlight = false;
      }
    };

    const id = setInterval(tick, 1000);
    tick();
    return () => { mounted = false; clearInterval(id); };
  }, [logsResetKey, localOnline, activeTab, lastRun?.id]);

  const clearLogs = async () => {
    if (!lastRun?.id) return;
    if (!window.confirm(
      `Удалить все логи run'а «${lastRun.template_name || lastRun.id}»?\n\n` +
      `Действие необратимо.`
    )) return;
    setClearingLogs(true);
    try {
      await SenderApi.deleteAllLogs(lastRun.id);
      setLogs([]);
      logCursorRef.current = 0;
      setLogsResetKey((k) => k + 1);
      add?.("🧹 Логи очищены", "success");
    } catch (e) {
      add?.("Ошибка очистки: " + e.message, "error");
    } finally {
      setClearingLogs(false);
    }
  };

  const refreshWorkers = async () => {
    const arr = await loadWorkers();
    if (arr.length) add?.(`👥 Воркеров: ${arr.length}`, "info");
  };

  const refreshGlobalRuntimes = async () => {
    await loadGlobalRuntimes();
  };

  const openPlanForRun = () => {
    if (!selectedRunId) return;
    if (!localOnline) {
      add?.("Локальный бекенд 127.0.0.1:17873 не запущен.", "warn");
      return;
    }
    setPlanTarget({ runId: selectedRunId });
    setPlanOpen(true);
  };

  // Шапка доступна сразу — не ждём initialLoading
  const stKey = localOnline === false ? "offline" : (status.state || "idle");
  const st = STATUS_CFG[stKey] || STATUS_CFG.idle;
  const p = status.progress || {};

  return (
    <div className="outreach-dashboard">
      <div className="rules-header">
        <div>
          <h2>📤 Sender</h2>
          <p>Сегменты и шаблоны — глобально · отправка — локально</p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center",
                      flexWrap: "wrap" }}>
          <span title={localOnline ? "Локальный отправитель доступен"
                                   : "127.0.0.1:17873 не запущен"}
                style={{ padding: "6px 14px", borderRadius: 8, fontSize: 13,
                         fontWeight: 700, background: st.bg, color: st.color }}>
            {st.label}
            {status.test_run && status.state === "running" && " · TEST"}
          </span>
          <button className="btn-cancel"
                  style={{ padding: "4px 10px", fontSize: 12 }}
                  onClick={() => setPingKey((x) => x + 1)}>🔄</button>
          {status.current_run_name && (
            <span style={{ fontSize: 12, color: "#6b7280" }}>
              · {status.current_run_name}
            </span>
          )}
        </div>
      </div>

      <div className="outreach-stats-grid">
        <StatCard icon="📤" value={p.run_sent} label="Отправлено"
                  sub={`из ${p.run_limit || "∞"}`} color="#16a34a" />
        <StatCard icon="🧪" value={p.test} label="Тестовых" color="#f59e0b" />
        <StatCard icon="❌" value={p.failed} label="Failed" color="#dc2626" />
        <StatCard icon="❓" value={p.unknown} label="Unknown" color="#a855f7" />
        <StatCard icon="⏭" value={p.skipped} label="Skipped" color="#6b7280" />
      </div>

      <div className="outreach-tabs">
        {[
          ["templates", `📦 Шаблоны (${templates.length})`],
          ["runs", "🚀 Запуски"],
          ["log", `📜 Журнал${logs.length ? ` (${logs.length})` : ""}`],
          ["segments", `🗂 Сегменты (${segments.length})`],
          ["runtimes", `🖥 Runtimes (${runtimes.length})`],
          ["settings", "⚙️ Настройки"],
        ].map(([k, lab]) => (
          <button key={k}
                  className={`outreach-tab ${activeTab === k ? "active" : ""}`}
                  onClick={() => setActiveTab(k)}>{lab}</button>
        ))}
      </div>

      {initialLoading && (
        <div style={{
          padding: "8px 16px", marginBottom: 8,
          background: "#fef3c7", border: "1px solid #fde68a",
          borderRadius: 8, fontSize: 12, color: "#92400e",
        }}>
          ⏳ Первичная загрузка данных… (можно работать, если данные
          уже видны)
        </div>
      )}

      {activeTab === "templates" && (
        
                <SenderTemplatesTab
          templates={templates}
          setTemplates={setTemplates}
          segments={segments}
          workers={workers}
          campaigns={campaigns}
          runtimes={runtimes}
          emailAccounts={emailAccounts}
          workersError={workersError}
          onRefreshWorkers={refreshWorkers}
          onLaunched={(lr) => {
            setSelectedRunId(lr.id);
            setActiveTab("runs");
            setPingKey((x) => x + 1);
          }}
          add={add}
        />
        
      )}
      {activeTab === "runs" && (
        <SenderLocalRunsTab
          selectedRunId={selectedRunId}
          setSelectedRunId={setSelectedRunId}
          onShowPlan={openPlanForRun}
          status={status}
          runtimes={runtimes}
          add={add}
        />
      )}
      {activeTab === "log" && (
        <SenderLogTab
          logs={logs}
          lastRun={lastRun}
          runtimes={runtimes}
          onClear={clearLogs}
          clearing={clearingLogs}
          onMessageClick={(info) => setMessageInfo(info)}
        />
      )}
      {activeTab === "segments" && (
        <SenderSegmentsTab
          segments={segments} setSegments={setSegments}
          campaigns={campaigns}
          add={add}
        />
      )}
      {activeTab === "runtimes" && (
        <SenderRuntimesTab
          runtimes={runtimes}
          setRuntimes={setRuntimes}
          localRuntimes={localRuntimes}
          onRefreshGlobal={refreshGlobalRuntimes}
          localOnline={localOnline}
          add={add}
        />
      )}
      {activeTab === "settings" && (
        <SenderSettingsTab
          settings={settings} setSettings={setSettings}
          add={add}
        />
      )}

      {messageInfo && (
        <MessageDetailDialog info={messageInfo}
          onClose={() => setMessageInfo(null)} />
      )}
      {testSendOpen && (
        <TestSendDialog workers={workers} add={add}
          onClose={() => setTestSendOpen(false)} />
      )}
      {planOpen && planTarget && (
        <SenderPlanModal runId={planTarget.runId} add={add}
          onClose={() => { setPlanOpen(false); setPlanTarget(null); }} />
      )}
      {finishedInfo && (
        <SenderFinishedModal info={finishedInfo}
          onClose={() => setFinishedInfo(null)} />
      )}
    </div>
  );
}

function StatCard({ icon, value, label, sub, color }) {
  const v = value ?? 0;
  return (
    <div className="outreach-stat-card"
         style={{ borderLeft: `4px solid ${color || "#3b71fe"}` }}>
      <div className="stat-icon">{icon}</div>
      <div className="stat-value" style={{ color: color || "#3b71fe" }}>
        {typeof v === "number" ? v.toLocaleString("ru-RU") : v}
      </div>
      <div className="stat-label">{label}</div>
      {sub && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}